import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Send, AlertTriangle } from "lucide-react";
import { useLang } from "@/lib/useLang";
import { getOrCreateVoterId } from "@/lib/voter";
import InsightsStep from "@/components/InsightsStep";
import ThankYouShare from "@/components/ThankYouShare";
import TurnstileWidget from "@/components/TurnstileWidget";
import Analytics from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_CHARS = 500;
const MIN_CHARS = 10;
const cpLength = (s) => [...(s || "")].length;

export const MessageSubmitForm = ({ onSubmitted }) => {
  const { t, lang } = useLang();
  const [voterId] = useState(getOrCreateVoterId);
  const [text, setText] = useState("");
  const [category, setCategory] = useState("");
  const [touchedCategory, setTouchedCategory] = useState(false);
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | submitting | step2 | thanks | error
  const [errorMsg, setErrorMsg] = useState("");
  const [lastSubmitted, setLastSubmitted] = useState(null);
  const turnstileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => {
      setCategories(r.data?.categories || []);
    }).catch(() => {});
  }, []);

  const len = useMemo(() => cpLength(text), [text]);
  const overLimit = len > MAX_CHARS;
  const tooShort = len < MIN_CHARS;
  const noCategory = !category;
  const disabled = status === "submitting" || overLimit || tooShort || noCategory;

  const reset = () => {
    setText("");
    setCategory("");
    setTouchedCategory(false);
    setStatus("idle");
    setErrorMsg("");
    setLastSubmitted(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouchedCategory(true);
    setErrorMsg("");
    if (disabled) return;
    setStatus("submitting");
    let token = null;
    try { token = await turnstileRef.current?.getToken(); } catch { /* ignore */ }
    if (!token) {
      setStatus("error");
      setErrorMsg("Please complete the bot check below the form and try again.");
      // bring widget into view
      setTimeout(() => {
        const el = document.querySelector('[data-testid="submit-turnstile-wrap"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    try {
      const r = await axios.post(`${API}/messages`, {
        message: text.trim(),
        category,
        voter_id: voterId,
        turnstile_token: token,
      });
      if (typeof onSubmitted === "function") onSubmitted(r.data);
      setLastSubmitted(r.data);
      setText("");
      Analytics.messageSubmit(category);
      setStatus("step2");
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setErrorMsg(typeof detail === "string" && detail ? detail : t("submit_error_generic"));
    }
  };

  // Step 2 — demographics
  if (status === "step2") {
    return (
      <InsightsStep
        voterId={voterId}
        onDone={() => setStatus("thanks")}
        onPostAnother={reset}
      />
    );
  }

  // Step 3 — thank-you + share
  if (status === "thanks") {
    return (
      <ThankYouShare
        submittedMessage={lastSubmitted}
        onBackToFeed={() => {
          reset();
          // scroll to feed
          setTimeout(() => {
            const feed = document.querySelector('[data-testid="message-feed"]');
            if (feed) feed.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 100);
        }}
      />
    );
  }

  return (
    <section className="brutal-card p-5 md:p-7" data-testid="submit-form-section">
      <p className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-600">
        {t("submit_eyebrow")}
      </p>
      <h2
        className="font-display uppercase leading-none mt-2 mb-4"
        style={{ fontSize: "clamp(1.5rem, 3.5vw, 2.25rem)" }}
        data-testid="submit-heading"
      >
        {t("submit_prompt")}
      </h2>

      <div
        className="flex items-start gap-2 border-2 border-black p-3 mb-4 text-sm"
        style={{ background: "#FFF7E6" }}
        data-testid="civility-notice"
      >
        <AlertTriangle size={18} strokeWidth={3} className="shrink-0 mt-0.5" style={{ color: "#E3000F" }} />
        <p className="font-medium leading-snug">{t("civility_notice")}</p>
      </div>

      <form onSubmit={handleSubmit} noValidate data-testid="submit-form">
        <label className="block text-xs uppercase font-bold tracking-[0.25em] mb-1.5">
          {t("submit_message_label")}
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("submit_placeholder")}
          maxLength={MAX_CHARS * 4}
          rows={4}
          disabled={status === "submitting"}
          className="w-full border-4 border-black p-3 text-base font-medium outline-none resize-y"
          style={{ background: "#FDFBF7", color: "#0A0A0A", minHeight: 110 }}
          data-testid="submit-textarea"
          aria-label={t("submit_message_label")}
        />
        <div className="flex justify-between items-center mt-1 text-xs font-bold tracking-wider">
          <span className="text-neutral-600 uppercase">
            {tooShort ? t("submit_too_short") : ""}
          </span>
          <span
            className={overLimit ? "text-[#E3000F]" : "text-neutral-600"}
            data-testid="char-counter"
          >
            {len} / {MAX_CHARS}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 mt-4">
          <div>
            <label className="block text-xs uppercase font-bold tracking-[0.25em] mb-1.5">
              {t("submit_category_label")}
            </label>
            <select
              value={category}
              onChange={(e) => { setCategory(e.target.value); setTouchedCategory(true); }}
              onBlur={() => setTouchedCategory(true)}
              disabled={status === "submitting"}
              required
              className="w-full border-4 px-3 py-3 text-base font-bold outline-none"
              style={{
                background: "#FDFBF7",
                color: category ? "#0A0A0A" : "#6B6B6B",
                borderColor: touchedCategory && noCategory ? "#E3000F" : "#0A0A0A",
                minHeight: 56,
              }}
              data-testid="submit-category"
              aria-invalid={touchedCategory && noCategory}
            >
              <option value="" disabled>
                {t("submit_category_placeholder")}
              </option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {lang === "ml" && c.name_ml ? c.name_ml : c.name}
                </option>
              ))}
            </select>
            {touchedCategory && noCategory && (
              <p className="text-xs font-bold mt-1" style={{ color: "#E3000F" }} data-testid="category-required-error">
                {t("submit_category_required")}
              </p>
            )}
          </div>
          <div className="flex flex-col justify-end">
            <button
              type="submit"
              disabled={disabled}
              className="brutal-btn px-6 shrink-0 disabled:opacity-50"
              style={{ minHeight: 56 }}
              data-testid="submit-button"
            >
              <Send size={18} strokeWidth={3} className="mr-2" />
              {status === "submitting" ? t("submit_submitting") : t("submit_button")}
            </button>
          </div>
        </div>
        <div className="mt-4 flex justify-start md:justify-end" data-testid="submit-turnstile-wrap">
          <TurnstileWidget ref={turnstileRef} />
        </div>
      </form>

      {status === "error" && errorMsg && (
        <div
          className="mt-4 border-2 border-black p-3"
          style={{ background: "#FFE4E6", color: "#0A0A0A" }}
          role="alert"
          data-testid="submit-error"
        >
          <p className="text-sm font-bold leading-snug">{errorMsg}</p>
        </div>
      )}
    </section>
  );
};

export default MessageSubmitForm;
